import React, { useCallback, useState } from 'react';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Spin, Form, Rate, message } from 'antd';
import { StarOutlined, PlusOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import Markdown from '@/components/ui/Markdown';
import { useProjectFeedback } from '@hooks/projects/useProjectFeedback';
import { formatDate, getFeedbackContentHtml } from '@hooks/projects/projectHelpers';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ChatMessageList,
    ChatMessageRow,
    ChatMessageAvatar,
    ChatMessageCol,
    ChatMessageBody,
    ChatMessageTime,
    MutedText,
} from '@/pages/inside/tenant/style';

function FeedbackContentEditor(props) {
    return <RichTextEditor placeholder="Share your feedback (you can add emojis)…" showEmojiPicker {...props} />;
}

/**
 * Project feedback list + add modal — GET/POST via `useProjectFeedback` (same as Feedback tab).
 */
export default function ProjectFeedbackFeed({ projectId }) {
    const { canView, canCreate, feedback, isLoading, submitFeedbackFromForm, createFeedbackPending } =
        useProjectFeedback(projectId);
    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        form.resetFields();
    }, [form]);

    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            submitFeedbackFromForm(values, {
                onSuccess: () => {
                    message.success('Feedback submitted');
                    closeModal();
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to submit feedback'),
                onValidationError: (msg) => message.warning(msg),
            });
        });
    }, [form, submitFeedbackFromForm, closeModal]);

    if (!canView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <StarOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>You don&apos;t have permission to view feedback</EmptyStateTitle>
                <EmptyStateDescription>Contact your admin if you need access.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }

    const addButton = canCreate ? (
        <Button
            type="button"
            $primary
            $blackOnLight
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            $size="13px"
            $padding="8px 16px"
        >
            Add feedback
        </Button>
    ) : null;

    return (
        <>
            <Container $display="flex" $flexDirection="column" $flex="1" $minHeight="0" style={{ minHeight: 0 }}>
                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="center"
                    $flexWrap="wrap"
                    $gap="12px"
                    $marginBottom="16px"
                >
                    <MutedText style={{ margin: 0, fontSize: 13 }}>
                        {feedback.length} feedback {feedback.length !== 1 ? 'entries' : 'entry'}
                    </MutedText>
                    {addButton}
                </Container>
                <ChatMessageList style={{ flex: 1, minHeight: 0 }}>
                    {feedback.length === 0 ? (
                        <EmptyStateBlock>
                            <EmptyStateIcon>
                                <StarOutlined />
                            </EmptyStateIcon>
                            <EmptyStateTitle>No feedback yet</EmptyStateTitle>
                            <EmptyStateDescription>
                                Add feedback with a rating using the button above.
                            </EmptyStateDescription>
                            {canCreate && <div style={{ marginTop: 16 }}>{addButton}</div>}
                        </EmptyStateBlock>
                    ) : (
                        feedback.map((item) => {
                            const html = getFeedbackContentHtml(item);
                            const created = item.createdAt ?? item.created_at;
                            const rating = item.rating ?? 0;
                            return (
                                <ChatMessageRow key={item.id ?? item.createdAt ?? Math.random()}>
                                    <ChatMessageAvatar>
                                        <StarOutlined style={{ fontSize: 14 }} />
                                    </ChatMessageAvatar>
                                    <ChatMessageCol>
                                        <ChatMessageBody>
                                            <Markdown source={html} emptyFallback="—" component="div" />
                                        </ChatMessageBody>
                                        <div style={{ marginTop: 4 }}>
                                            <Rate disabled value={rating} count={5} style={{ fontSize: 14 }} />
                                        </div>
                                        {created != null && (
                                            <ChatMessageTime>{formatDate(created) ?? '—'}</ChatMessageTime>
                                        )}
                                    </ChatMessageCol>
                                </ChatMessageRow>
                            );
                        })
                    )}
                </ChatMessageList>
            </Container>

            <AppModal size="md" title="Add feedback" open={modalOpen} onCancel={closeModal} footer={null}>
                <CreateRoleModalBody>
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="rating"
                            label="Rating"
                            rules={[{ required: true, message: 'Select a rating (1–5 stars)' }]}
                        >
                            <Rate count={5} />
                        </Form.Item>
                        <Form.Item
                            name="content"
                            label="Feedback"
                            rules={[{ required: true, message: 'Enter your feedback' }]}
                        >
                            <FeedbackContentEditor />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="14px" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button $primary $blackOnLight $size="14px" loading={createFeedbackPending} onClick={handleSubmit}>
                        Submit
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </>
    );
}
